/**
 * Cluster — Santé du cluster k8s
 * Score bar (good/warn/crit), liste des nœuds avec rôles/statut,
 * timeline des scores. Données placeholder si l'API n'est pas disponible.
 */

import { useState, useEffect } from "react";
import { api } from "../lib/api";

type HealthLevel = "good" | "warn" | "crit";

interface ClusterNode {
  name: string;
  role: "master" | "worker";
  status: "Ready" | "NotReady";
  cpu: number; // %
  memory: number; // %
  disk: number; // %
  version: string;
  uptime: string;
}

interface ScoreEntry {
  id: string;
  date: string;
  score: number;
  level: HealthLevel;
  summary: string;
}

function levelFromScore(score: number): HealthLevel {
  if (score >= 80) return "good";
  if (score >= 60) return "warn";
  return "crit";
}

export default function Cluster() {
  const [nodes, setNodes] = useState<ClusterNode[]>([]);
  const [scores, setScores] = useState<ScoreEntry[]>([]);
  const [currentScore, setCurrentScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedScoreId, setSelectedScoreId] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.cluster.health();
      const nodeData = (data.nodes ?? []) as ClusterNode[];
      const scoreData = (data.history ?? []) as ScoreEntry[];
      setNodes(nodeData);
      setScores(scoreData);
      setCurrentScore(data.score ?? scoreData[0]?.score ?? null);
      setSelectedScoreId(scoreData[0]?.id ?? null);
      // OBS-02: état honnête — pas de données fabriquées quand indisponible.
      setError(data.available ? null : (data.detail ?? "Métriques cluster non disponibles."));
    } catch (err) {
      setNodes([]);
      setScores([]);
      setCurrentScore(null);
      setSelectedScoreId(null);
      setError(err instanceof Error ? err.message : "Cluster injoignable.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const scoreLevel = currentScore !== null ? levelFromScore(currentScore) : "warn";
  const readyNodes = nodes.filter((n) => n.status === "Ready").length;
  const masterCount = nodes.filter((n) => n.role === "master").length;
  const workerCount = nodes.filter((n) => n.role === "worker").length;
  const avgCpu = readyNodes > 0 ? Math.round(nodes.filter((n) => n.status === "Ready").reduce((a, n) => a + n.cpu, 0) / readyNodes) : 0;
  const avgMem = readyNodes > 0 ? Math.round(nodes.filter((n) => n.status === "Ready").reduce((a, n) => a + n.memory, 0) / readyNodes) : 0;

  const selectedScore = scores.find((s) => s.id === selectedScoreId);

  return (
    <div className="page active" style={{ display: "grid", gap: "14px" }}>
      <div className="page-header">
        <div>
          <div className="eyebrow">infrastructure</div>
          <h1>Cluster</h1>
          <p>
            {nodes.length} nœuds · {readyNodes} prêts · {masterCount} master · {workerCount} worker
          </p>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={load}>↻ Actualiser</button>
        </div>
      </div>

      {error && (
        <div className="ops-card" style={{ borderColor: "var(--amber)" }}>
          <p style={{ color: "var(--amber)" }}>⚠ {error}</p>
        </div>
      )}

      {loading && <div className="no-results">Chargement de l'état du cluster…</div>}

      {/* OBS-02: état vide honnête quand aucune métrique réelle n'est disponible */}
      {!loading && nodes.length === 0 && (
        <div className="ops-card" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>📡</div>
          <p style={{ color: "var(--muted)", margin: 0 }}>
            Aucune métrique cluster à afficher pour le moment.
          </p>
          <p style={{ color: "var(--muted)", fontSize: "12px", marginTop: "4px" }}>
            Les métriques temps réel (metrics-server) seront branchées prochainement.
          </p>
        </div>
      )}

      {/* Score bar */}
      {!loading && nodes.length > 0 && (
        <>
          <div className="stats-row" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
            <div className="stat-card">
              <div className="eyebrow">score global</div>
              <div className="val" style={{ color: `var(--${scoreLevel === "good" ? "olive" : scoreLevel === "warn" ? "amber" : "rust"})` }}>
                {currentScore ?? "—"}
              </div>
              <div className="sub">{scoreLevel === "good" ? "nominal" : scoreLevel === "warn" ? "attention" : "critique"}</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">nœuds prêts</div>
              <div className="val">{readyNodes}/{nodes.length}</div>
              <div className="sub">{nodes.length - readyNodes} NotReady</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">cpu moyen</div>
              <div className="val" style={{ color: avgCpu > 80 ? "var(--rust)" : avgCpu > 60 ? "var(--amber)" : "var(--olive)" }}>{avgCpu}%</div>
              <div className="sub">utilisation</div>
            </div>
            <div className="stat-card">
              <div className="eyebrow">mémoire moy.</div>
              <div className="val" style={{ color: avgMem > 80 ? "var(--rust)" : avgMem > 60 ? "var(--amber)" : "var(--olive)" }}>{avgMem}%</div>
              <div className="sub">utilisation</div>
            </div>
          </div>

          {/* Score bar visualisation */}
          <div className="ops-card">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
              <span className="eyebrow">score de santé</span>
              <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--muted)" }}>
                {currentScore}/100
              </span>
            </div>
            <div className="cluster-score-bar">
              <div
                className={`cluster-score-fill ${scoreLevel}`}
                style={{ width: `${currentScore ?? 0}%` }}
              />
            </div>
            <div className="cluster-score-labels">
              <span>0 critique</span>
              <span>60 attention</span>
              <span>80 nominal</span>
              <span>100</span>
            </div>
          </div>

          {/* Node list + Timeline */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "14px", alignItems: "start" }}>
            {/* Nodes */}
            <div className="ops-card">
              <div className="eyebrow" style={{ marginBottom: "10px" }}>nœuds</div>
              <div className="ops-list">
                {nodes.map((node) => (
                  <div key={node.name} className="cluster-node">
                    <span
                      className="dot-sm"
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "999px",
                        background: node.status === "Ready" ? "var(--olive)" : "var(--rust)",
                        flexShrink: 0,
                      }}
                    />
                    <span className="cluster-node-name">{node.name}</span>
                    <span className={`cluster-node-role ${node.role === "worker" ? "worker" : ""}`}>{node.role}</span>
                    <div className="cluster-node-status">
                      <span className="dot" style={{ background: node.status === "Ready" ? "var(--olive)" : "var(--rust)" }} />
                      {node.status === "Ready" ? `${node.cpu}% CPU` : "NotReady"}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div className="ops-card">
              <div className="eyebrow" style={{ marginBottom: "10px" }}>historique des scores</div>
              <div className="ops-list">
                {scores.map((s) => (
                  <div
                    key={s.id}
                    className={`cluster-timeline-item ${selectedScoreId === s.id ? "active" : ""}`}
                    onClick={() => setSelectedScoreId(s.id)}
                  >
                    <div className={`cluster-timeline-score ${s.level}`}>{s.score}</div>
                    <div style={{ minWidth: 0 }}>
                      <div className="cluster-timeline-date">
                        {new Date(s.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                      </div>
                      <div style={{ fontSize: "11px", color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {s.summary}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Selected score detail */}
          {selectedScore && (
            <div className="ops-card">
              <div className="eyebrow" style={{ marginBottom: "8px" }}>détail du snapshot</div>
              <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
                <div className={`cluster-timeline-score ${selectedScore.level}`} style={{ width: "48px", height: "48px", fontSize: "16px" }}>
                  {selectedScore.score}
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 600 }}>{selectedScore.summary}</div>
                  <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--muted)" }}>
                    {new Date(selectedScore.date).toLocaleString("fr-FR")}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
